/**
 * Impression tracking with macros — operator UI (feature #168).
 *
 * Composition:
 *   - Header with emerald->sky gradient + subtitle.
 *   - Create template card: label, URL template, advertiser/campaign scope,
 *     events_csv, enabled toggle, live preview of the rendered URL as the
 *     operator types (client-side render to avoid a round trip per keystroke).
 *   - Templates table: label, scope, events_csv, updated_at, status,
 *     delete action.
 *   - Recent impressions card: latest audit rows with template + click filter.
 *   - Summary facets: totals + top geo / device / os / event.
 *
 * Data layer: TanStack Query for fetch + mutations. All validation mirrors
 * the server so the operator gets feedback pre-round-trip.
 *
 * Brand: emerald->sky headline gradient, zinc borders, 6-8px rounded, light
 * theme only, no emojis, no competitor names.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	createImpressionTemplate,
	deleteImpressionTemplate,
	extractMacroKeys,
	formatEnabledLabel,
	formatInteger,
	formatTimestamp,
	IMPRESSION_SUPPORTED_MACROS,
	impressionTrackingQueryKeys,
	isValidEventsCsv,
	isValidLabel,
	isValidUrlTemplate,
	listImpressions,
	listImpressionTemplates,
	renderMacrosClient,
	sortImpressionsNewestFirst,
	sortTemplates,
	summariseImpressions,
	updateImpressionTemplate,
	type ImpressionFilters,
	type ImpressionMacroContext,
	type ImpressionRow,
	type ImpressionSummary,
	type ImpressionTemplate,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — zinc borders, 6-8px rounded, light only.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const INPUT_MONO =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 font-mono focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-500 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE_OK =
	"inline-flex items-center rounded-[4px] border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700";
const BADGE_OFF =
	"inline-flex items-center rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-xs font-medium text-zinc-600";
const HEADLINE =
	"bg-gradient-to-r from-emerald-600 to-sky-500 bg-clip-text text-transparent";

// ---------------------------------------------------------------------------
// Default preview context — used by the live preview so operators see a
// representative rendering rather than a URL full of empty query params.
// ---------------------------------------------------------------------------

const DEFAULT_PREVIEW_CONTEXT: ImpressionMacroContext = {
	click_id: "c_demo_123",
	campaign_id: "cmp_spring",
	affiliate_id: "aff_42",
	advertiser_id: "adv_acme",
	geo: "US",
	device: "mobile",
	os: "ios",
	event: "view",
	ts: 1_700_000_000,
	rnd: "a1b2c3d4e5f6",
	template_id: "imp_tpl_preview",
	sub1: "sp1",
	sub2: "sp2",
	sub3: "sp3",
	sub4: "",
	sub5: "",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ImpressionTrackingPageProps {
	/** Optional advertiser scope to pre-fill the template form with. */
	initialAdvertiserId?: string;
	/** Optional campaign scope to pre-fill the template form with. */
	initialCampaignId?: string;
}

export function ImpressionTrackingPage(
	props: ImpressionTrackingPageProps = {},
) {
	const queryClient = useQueryClient();

	const [label, setLabel] = useState("");
	const [urlTemplate, setUrlTemplate] = useState(
		"https://pixel.example/imp?cid={click_id}&cmp={campaign_id}" +
			"&aff={affiliate_id}&g={geo}&d={device}&os={os}" +
			"&e={event}&t={ts}&r={rnd}",
	);
	const [advertiserId, setAdvertiserId] = useState(
		props.initialAdvertiserId ?? "",
	);
	const [campaignId, setCampaignId] = useState(props.initialCampaignId ?? "");
	const [eventsCsv, setEventsCsv] = useState("*");
	const [enabled, setEnabled] = useState<0 | 1>(1);

	// Sync the scope fields when the URL-level props change — lets the parent
	// deep-link into advertiser + campaign specific template rosters.
	useEffect(() => {
		if (props.initialAdvertiserId !== undefined) {
			setAdvertiserId(props.initialAdvertiserId);
		}
		if (props.initialCampaignId !== undefined) {
			setCampaignId(props.initialCampaignId);
		}
	}, [props.initialAdvertiserId, props.initialCampaignId]);

	const filters = useMemo<ImpressionFilters>(() => {
		const f: ImpressionFilters = {};
		if (advertiserId.trim()) f.template_id = undefined; // reserved
		if (campaignId.trim()) f.campaign_id = campaignId.trim();
		return f;
	}, [advertiserId, campaignId]);

	// -------------------------------------------------------------------
	// Queries
	// -------------------------------------------------------------------
	const templatesQuery = useQuery({
		queryKey: impressionTrackingQueryKeys.templates({
			advertiser_id: advertiserId.trim() || undefined,
			campaign_id: campaignId.trim() || undefined,
		}),
		queryFn: () =>
			listImpressionTemplates({
				...(advertiserId.trim() ? { advertiser_id: advertiserId.trim() } : {}),
				...(campaignId.trim() ? { campaign_id: campaignId.trim() } : {}),
			}),
	});

	const impressionsQuery = useQuery({
		queryKey: impressionTrackingQueryKeys.impressions(filters),
		queryFn: () => listImpressions(filters),
	});

	const summaryQuery = useQuery({
		queryKey: impressionTrackingQueryKeys.summary(filters),
		queryFn: () => summariseImpressions(filters),
	});

	const templates = useMemo(
		() => sortTemplates(templatesQuery.data?.templates ?? []),
		[templatesQuery.data?.templates],
	);
	const impressions = useMemo(
		() => sortImpressionsNewestFirst(impressionsQuery.data?.impressions ?? []),
		[impressionsQuery.data?.impressions],
	);
	const summary = useMemo<ImpressionSummary | null>(
		() => summaryQuery.data ?? null,
		[summaryQuery.data],
	);

	// -------------------------------------------------------------------
	// Mutations
	// -------------------------------------------------------------------
	const createMutation = useMutation({
		mutationFn: () =>
			createImpressionTemplate({
				label: label.trim(),
				url_template: urlTemplate.trim(),
				advertiser_id: advertiserId.trim(),
				campaign_id: campaignId.trim(),
				events_csv: eventsCsv.trim() || "*",
				enabled: enabled === 1,
			}),
		onSuccess: () => {
			toast.success("Template created.");
			setLabel("");
			queryClient.invalidateQueries({
				queryKey: impressionTrackingQueryKeys.all,
			});
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to create template."),
	});

	const toggleMutation = useMutation({
		mutationFn: (row: ImpressionTemplate) =>
			updateImpressionTemplate(row.id, { enabled: row.enabled !== 1 }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: impressionTrackingQueryKeys.all,
			});
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to toggle template."),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteImpressionTemplate(id),
		onSuccess: () => {
			toast.success("Template removed.");
			queryClient.invalidateQueries({
				queryKey: impressionTrackingQueryKeys.all,
			});
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to remove template."),
	});

	const busy =
		createMutation.isPending ||
		toggleMutation.isPending ||
		deleteMutation.isPending;

	// -------------------------------------------------------------------
	// Handlers
	// -------------------------------------------------------------------
	const macroKeys = useMemo(() => extractMacroKeys(urlTemplate), [urlTemplate]);
	const preview = useMemo(
		() => renderMacrosClient(urlTemplate, DEFAULT_PREVIEW_CONTEXT),
		[urlTemplate],
	);

	const handleCreate = useCallback(() => {
		if (!isValidLabel(label)) {
			toast.error("Label required (<=120 bytes).");
			return;
		}
		if (!isValidUrlTemplate(urlTemplate)) {
			toast.error("URL template must be a valid http(s) URL (<=2 KB).");
			return;
		}
		if (!isValidEventsCsv(eventsCsv)) {
			toast.error(
				"Events CSV must be '*' or a comma list of [A-Za-z0-9_.-] tokens.",
			);
			return;
		}
		createMutation.mutate();
	}, [label, urlTemplate, eventsCsv, createMutation]);

	const handleToggle = useCallback(
		(row: ImpressionTemplate) => {
			if (busy) return;
			toggleMutation.mutate(row);
		},
		[busy, toggleMutation],
	);

	const handleDelete = useCallback(
		(row: ImpressionTemplate) => {
			if (busy) return;
			deleteMutation.mutate(row.id);
		},
		[busy, deleteMutation],
	);

	// -------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------
	return (
		<section className="flex flex-col gap-4">
			<header className="flex flex-col gap-1">
				<h1 className={`text-2xl font-semibold ${HEADLINE}`}>
					Impression tracking
				</h1>
				<p className="text-sm text-zinc-600">
					Register impression URL templates with curly-brace macros. At
					serve time we substitute the macros against the serve context,
					persist an audit row, and return the rendered URL for the tag
					manager to fire.
				</p>
			</header>

			<CreateTemplateCard
				label={label}
				urlTemplate={urlTemplate}
				advertiserId={advertiserId}
				campaignId={campaignId}
				eventsCsv={eventsCsv}
				enabled={enabled}
				preview={preview}
				macroKeys={macroKeys}
				busy={busy}
				pending={createMutation.isPending}
				onChangeLabel={setLabel}
				onChangeUrlTemplate={setUrlTemplate}
				onChangeAdvertiserId={setAdvertiserId}
				onChangeCampaignId={setCampaignId}
				onChangeEventsCsv={setEventsCsv}
				onToggleEnabled={() => setEnabled((v) => (v === 1 ? 0 : 1))}
				onSubmit={handleCreate}
			/>

			{summary ? <SummaryRow summary={summary} /> : null}

			<TemplatesTable
				templates={templates}
				loading={templatesQuery.isLoading}
				error={templatesQuery.error as Error | undefined}
				busy={busy}
				onToggle={handleToggle}
				onDelete={handleDelete}
			/>

			<ImpressionsTable
				rows={impressions}
				loading={impressionsQuery.isLoading}
				error={impressionsQuery.error as Error | undefined}
				limit={impressionsQuery.data?.limit ?? 200}
			/>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Create-template card
// ---------------------------------------------------------------------------

interface CreateTemplateCardProps {
	label: string;
	urlTemplate: string;
	advertiserId: string;
	campaignId: string;
	eventsCsv: string;
	enabled: 0 | 1;
	preview: string;
	macroKeys: string[];
	busy: boolean;
	pending: boolean;
	onChangeLabel: (v: string) => void;
	onChangeUrlTemplate: (v: string) => void;
	onChangeAdvertiserId: (v: string) => void;
	onChangeCampaignId: (v: string) => void;
	onChangeEventsCsv: (v: string) => void;
	onToggleEnabled: () => void;
	onSubmit: () => void;
}

function CreateTemplateCard(props: CreateTemplateCardProps) {
	const known = new Set(IMPRESSION_SUPPORTED_MACROS as readonly string[]);
	const unknownMacros = props.macroKeys.filter((k) => !known.has(k));

	return (
		<div className={`${CARD} flex flex-col gap-4 p-4`}>
			<h2 className="text-base font-semibold text-zinc-900">
				Register a template
			</h2>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<div className="flex flex-col gap-1">
					<label htmlFor="imp-label" className={LABEL}>
						Label
					</label>
					<input
						id="imp-label"
						type="text"
						value={props.label}
						onChange={(e) => props.onChangeLabel(e.target.value)}
						className={INPUT}
						placeholder="Primary pixel"
						maxLength={180}
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="imp-events" className={LABEL}>
						Events (CSV or "*")
					</label>
					<input
						id="imp-events"
						type="text"
						value={props.eventsCsv}
						onChange={(e) => props.onChangeEventsCsv(e.target.value)}
						className={INPUT}
						placeholder="view,viewable"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="imp-advertiser" className={LABEL}>
						Advertiser id (optional scope)
					</label>
					<input
						id="imp-advertiser"
						type="text"
						value={props.advertiserId}
						onChange={(e) => props.onChangeAdvertiserId(e.target.value)}
						className={INPUT}
						placeholder="adv_acme"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="imp-campaign" className={LABEL}>
						Campaign id (optional scope)
					</label>
					<input
						id="imp-campaign"
						type="text"
						value={props.campaignId}
						onChange={(e) => props.onChangeCampaignId(e.target.value)}
						className={INPUT}
						placeholder="cmp_spring"
					/>
				</div>
				<div className="flex flex-col gap-1 sm:col-span-2">
					<label htmlFor="imp-url" className={LABEL}>
						URL template
					</label>
					<textarea
						id="imp-url"
						rows={3}
						value={props.urlTemplate}
						onChange={(e) => props.onChangeUrlTemplate(e.target.value)}
						className={INPUT_MONO}
						placeholder="https://pixel.example/imp?cid={click_id}"
					/>
					<p className="text-xs text-zinc-500">
						Supported macros:{" "}
						<span className="font-mono">
							{IMPRESSION_SUPPORTED_MACROS.map((m) => `{${m}}`).join(" ")}
						</span>
						. Unknown macros render as empty.
					</p>
					{unknownMacros.length > 0 ? (
						<p className="text-xs text-amber-700">
							Unknown macros in template:{" "}
							<span className="font-mono">
								{unknownMacros.map((k) => `{${k}}`).join(", ")}
							</span>
						</p>
					) : null}
				</div>
				<div className="flex items-center gap-3 sm:col-span-2">
					<button
						type="button"
						onClick={props.onToggleEnabled}
						className={BTN_NEUTRAL}
						aria-pressed={props.enabled === 1}
					>
						{props.enabled === 1 ? "Enabled" : "Disabled"} (toggle)
					</button>
					<span className="text-xs text-zinc-500">
						Disabled templates never render at serve time.
					</span>
				</div>
			</div>
			<div className="flex flex-col gap-2 rounded-[6px] border border-zinc-200 bg-zinc-50 p-3">
				<span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
					Live preview
				</span>
				<code className="break-all font-mono text-xs text-zinc-800">
					{props.preview || <span className="text-zinc-400">Type above to render.</span>}
				</code>
			</div>
			<div className="flex justify-end">
				<button
					type="button"
					onClick={props.onSubmit}
					disabled={props.busy || !props.label.trim() || !props.urlTemplate.trim()}
					className={BTN_PRIMARY}
				>
					{props.pending ? "Creating..." : "Create template"}
				</button>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Templates table
// ---------------------------------------------------------------------------

interface TemplatesTableProps {
	templates: readonly ImpressionTemplate[];
	loading: boolean;
	error: Error | undefined;
	busy: boolean;
	onToggle: (row: ImpressionTemplate) => void;
	onDelete: (row: ImpressionTemplate) => void;
}

function TemplatesTable(props: TemplatesTableProps) {
	return (
		<div className={`${CARD} overflow-hidden`}>
			<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
				<h2 className="text-base font-semibold text-zinc-900">Templates</h2>
				<span className="text-xs text-zinc-500">
					{formatInteger(props.templates.length)} total
				</span>
			</div>
			{props.loading ? (
				<div className="px-4 py-8 text-center text-sm text-zinc-500">
					Loading templates...
				</div>
			) : props.error ? (
				<div className="px-4 py-8 text-center text-sm text-red-600">
					{props.error.message || "Unable to load templates."}
				</div>
			) : props.templates.length === 0 ? (
				<div className="px-4 py-8 text-center text-sm text-zinc-500">
					No templates yet. Register one above to get started.
				</div>
			) : (
				<div className="overflow-x-auto">
					<table className="min-w-full divide-y divide-zinc-200 text-sm">
						<thead className="bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
							<tr>
								<th scope="col" className="px-4 py-2">Label</th>
								<th scope="col" className="px-4 py-2">Advertiser</th>
								<th scope="col" className="px-4 py-2">Campaign</th>
								<th scope="col" className="px-4 py-2">Events</th>
								<th scope="col" className="px-4 py-2">URL template</th>
								<th scope="col" className="px-4 py-2">Updated</th>
								<th scope="col" className="px-4 py-2">Status</th>
								<th scope="col" className="px-4 py-2 text-right">Actions</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100 bg-white">
							{props.templates.map((row) => (
								<tr key={row.id}>
									<td className="max-w-[180px] truncate px-4 py-2 text-zinc-900">
										{row.label}
									</td>
									<td className="px-4 py-2 text-zinc-700">
										{row.advertiser_id || (
											<span className="text-zinc-400">any</span>
										)}
									</td>
									<td className="px-4 py-2 text-zinc-700">
										{row.campaign_id || (
											<span className="text-zinc-400">any</span>
										)}
									</td>
									<td className="px-4 py-2 text-zinc-700">
										<span className="font-mono text-xs">{row.events_csv}</span>
									</td>
									<td className="max-w-[260px] truncate px-4 py-2 text-zinc-700">
										<span
											className="font-mono text-xs"
											title={row.url_template}
										>
											{row.url_template}
										</span>
									</td>
									<td className="px-4 py-2 text-zinc-700">
										{formatTimestamp(row.updated_at)}
									</td>
									<td className="px-4 py-2">
										<span
											className={row.enabled === 1 ? BADGE_OK : BADGE_OFF}
										>
											{formatEnabledLabel(row.enabled)}
										</span>
									</td>
									<td className="px-4 py-2 text-right">
										<div className="flex justify-end gap-2">
											<button
												type="button"
												className={BTN_NEUTRAL}
												onClick={() => props.onToggle(row)}
												disabled={props.busy}
											>
												{row.enabled === 1 ? "Disable" : "Enable"}
											</button>
											<button
												type="button"
												className={BTN_DANGER}
												onClick={() => props.onDelete(row)}
												disabled={props.busy}
											>
												Delete
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Recent impressions table
// ---------------------------------------------------------------------------

interface ImpressionsTableProps {
	rows: readonly ImpressionRow[];
	loading: boolean;
	error: Error | undefined;
	limit: number;
}

function ImpressionsTable(props: ImpressionsTableProps) {
	return (
		<div className={`${CARD} overflow-hidden`}>
			<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
				<h2 className="text-base font-semibold text-zinc-900">
					Recent impressions
				</h2>
				<span className="text-xs text-zinc-500">
					Showing {formatInteger(props.rows.length)} / {formatInteger(props.limit)}
				</span>
			</div>
			{props.loading ? (
				<div className="px-4 py-8 text-center text-sm text-zinc-500">
					Loading impressions...
				</div>
			) : props.error ? (
				<div className="px-4 py-8 text-center text-sm text-red-600">
					{props.error.message || "Unable to load impressions."}
				</div>
			) : props.rows.length === 0 ? (
				<div className="px-4 py-8 text-center text-sm text-zinc-500">
					No impressions served yet.
				</div>
			) : (
				<div className="overflow-x-auto">
					<table className="min-w-full divide-y divide-zinc-200 text-sm">
						<thead className="bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
							<tr>
								<th scope="col" className="px-4 py-2">When</th>
								<th scope="col" className="px-4 py-2">Template</th>
								<th scope="col" className="px-4 py-2">Click id</th>
								<th scope="col" className="px-4 py-2">Campaign</th>
								<th scope="col" className="px-4 py-2">Affiliate</th>
								<th scope="col" className="px-4 py-2">Geo</th>
								<th scope="col" className="px-4 py-2">Device</th>
								<th scope="col" className="px-4 py-2">OS</th>
								<th scope="col" className="px-4 py-2">Event</th>
								<th scope="col" className="px-4 py-2">Rendered URL</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100 bg-white">
							{props.rows.map((row) => (
								<tr key={row.id}>
									<td className="px-4 py-2 text-zinc-700">
										{formatTimestamp(row.created_at)}
									</td>
									<td className="px-4 py-2 text-zinc-700">
										<span className="font-mono text-xs">{row.template_id}</span>
									</td>
									<td className="px-4 py-2 text-zinc-700">
										<span className="font-mono text-xs">{row.click_id || "-"}</span>
									</td>
									<td className="px-4 py-2 text-zinc-700">
										{row.campaign_id || "-"}
									</td>
									<td className="px-4 py-2 text-zinc-700">
										{row.affiliate_id || "-"}
									</td>
									<td className="px-4 py-2 text-zinc-700">{row.geo || "-"}</td>
									<td className="px-4 py-2 text-zinc-700">{row.device || "-"}</td>
									<td className="px-4 py-2 text-zinc-700">{row.os || "-"}</td>
									<td className="px-4 py-2 text-zinc-700">{row.event}</td>
									<td className="max-w-[260px] truncate px-4 py-2 text-zinc-700">
										<span className="font-mono text-xs" title={row.rendered_url}>
											{row.rendered_url}
										</span>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Summary row
// ---------------------------------------------------------------------------

interface SummaryRowProps {
	summary: ImpressionSummary;
}

function SummaryRow({ summary }: SummaryRowProps) {
	const topOf = (
		facet: Array<{ key: string; count: number }>,
	): { key: string; count: number } | null => (facet.length > 0 ? facet[0]! : null);
	const items: Array<{ label: string; value: string; tone: "primary" | "neutral" }> = [
		{ label: "Total", value: formatInteger(summary.total), tone: "primary" },
		{
			label: "Top geo",
			value: topOf(summary.by_geo)
				? `${topOf(summary.by_geo)!.key || "(unset)"} (${formatInteger(topOf(summary.by_geo)!.count)})`
				: "--",
			tone: "neutral",
		},
		{
			label: "Top device",
			value: topOf(summary.by_device)
				? `${topOf(summary.by_device)!.key || "(unset)"} (${formatInteger(topOf(summary.by_device)!.count)})`
				: "--",
			tone: "neutral",
		},
		{
			label: "Top OS",
			value: topOf(summary.by_os)
				? `${topOf(summary.by_os)!.key || "(unset)"} (${formatInteger(topOf(summary.by_os)!.count)})`
				: "--",
			tone: "neutral",
		},
		{
			label: "Top event",
			value: topOf(summary.by_event)
				? `${topOf(summary.by_event)!.key || "(unset)"} (${formatInteger(topOf(summary.by_event)!.count)})`
				: "--",
			tone: "primary",
		},
	];
	return (
		<div className={`${CARD} grid grid-cols-2 gap-0 p-0 sm:grid-cols-5`}>
			{items.map((it, i) => (
				<div
					key={it.label}
					className={
						"flex flex-col gap-1 px-4 py-3 " +
						(i > 0
							? "border-t border-zinc-200 sm:border-l sm:border-t-0"
							: "")
					}
				>
					<span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						{it.label}
					</span>
					<span
						className={
							"text-base font-semibold " +
							(it.tone === "primary" ? "text-emerald-700" : "text-zinc-900")
						}
					>
						{it.value}
					</span>
				</div>
			))}
		</div>
	);
}

export default ImpressionTrackingPage;
